import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fetchHeadToHead } from "@/lib/edgeFunctions";
import { toast } from "sonner";

interface UseAutoFetchH2HProps {
  teamHomeApiId: number | null;
  teamAwayApiId: number | null;
  hasExistingData: boolean;
  enabled?: boolean;
}

export const useAutoFetchH2H = ({
  teamHomeApiId,
  teamAwayApiId,
  hasExistingData,
  enabled = true
}: UseAutoFetchH2HProps) => {
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const queryClient = useQueryClient();

  // Reset hasFetchedOnce when teams change
  useEffect(() => {
    setHasFetchedOnce(false);
    setError(null);
  }, [teamHomeApiId, teamAwayApiId]);

  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if:
      // - Already has data
      // - Already tried fetching once (prevents infinite loop when no H2H exists)
      // - Missing API IDs
      // - Already fetching
      // - Disabled
      if (hasExistingData || hasFetchedOnce || !teamHomeApiId || !teamAwayApiId || isFetching || !enabled) {
        return;
      }

      console.log('🔄 Auto-fetching H2H data for teams:', teamHomeApiId, teamAwayApiId);
      setIsFetching(true);
      setError(null);

      try {
        const result = await fetchHeadToHead(teamHomeApiId, teamAwayApiId, 10);
        
        // Mark as fetched BEFORE any query invalidation
        setHasFetchedOnce(true);
        
        if (result.success) {
          console.log('✅ H2H data fetched successfully:', result.stats);
          
          // Only invalidate if we actually got data
          if (result.stats && result.stats.total_games > 0) {
            queryClient.invalidateQueries({
              queryKey: ['head-to-head']
            });
          } else {
            console.log('ℹ️ No H2H history exists between these teams');
          }
        } else {
          throw new Error(result.error || 'Failed to fetch Head-to-Head data');
        }
      } catch (err: any) {
        const errorMsg = 'Failed to fetch Head-to-Head data';
        console.error('❌ Error fetching H2H:', err);
        setError(errorMsg);
        setHasFetchedOnce(true); // Also mark as fetched on error to prevent retry loop
        toast.error(errorMsg);
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [teamHomeApiId, teamAwayApiId, hasExistingData, hasFetchedOnce, enabled, isFetching, queryClient]);

  return {
    isFetching,
    error,
    hasFetchedOnce
  };
};
